import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Download, Edit, FileSignature, Plus, Search,
  Settings2, ShieldAlert, Trash2, Upload, X,
} from "lucide-react";

/**
 * Il POS generale di appalto: quello che si manda al committente quando il
 * contratto parte.
 *
 * Non è il POS di cantiere. Quello descrive un indirizzo, questo descrive
 * l'impresa per tutta la durata dell'appalto: chi ci lavora, con che mezzi,
 * con quali qualifiche di saldatura, in che tempi.
 *
 * Prima si ricopiava a mano da un Word sul disco di rete, e le tre tabelle che
 * costano più fatica — il personale, i mezzi, i saldatori — erano anche le tre
 * che invecchiavano per prime. Qui si spuntano da un elenco che l'app ha già.
 */
const itDate = (iso) =>
  (iso ? String(iso).substring(0, 10).split("-").reverse().join("/") : "—");

const vuoto = () => ({
  committente_id: "", committente: "", committente_persona: "",
  committente_qualifica: "", opera: "", cig: "", contratto: "",
  ordine_lavoro: "", pl_numero: "", indirizzo: "", responsabile_lavori: "",
  csp: "", cse: "", subappalto: "", campo_subappalto: "", note: "",
  personale_ids: [], mezzi_ids: [], saldatori: [], noleggio: [], fasi: [],
});

const nomeMezzo = (m) =>
  [m.category, m.brand, m.model].filter(Boolean).join(" ") || m.identifier;

/* Le valutazioni dei rischi specifici, e come si vede a colpo d'occhio
   quale sta per scadere. Nell'app ci sono solo gli estremi: i documenti
   sono decine di pagine di fonometrie, l'originale resta in sede. */
const STATO_VALUTAZIONE = {
  scaduta: { testo: "Scaduta", classe: "bg-red-100 text-red-700 border-red-200" },
  in_scadenza: { testo: "In scadenza", classe: "bg-amber-100 text-amber-800 border-amber-200" },
  valida: { testo: "Valida", classe: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  senza_scadenza: { testo: "Senza scadenza", classe: "bg-slate-100 text-slate-600 border-slate-200" },
};

/* I campi stanno fuori dal componente: dichiarati dentro, React li ricrea a
   ogni battuta e il cursore salta via dalla casella. */
function Campo({ etichetta, valore, onChange, larga, ...resto }) {
  return (
    <div className={larga ? "sm:col-span-2" : ""}>
      <Label className="text-xs text-slate-500">{etichetta}</Label>
      <Input value={valore || ""} onChange={(e) => onChange(e.target.value)}
             className="mt-1" {...resto} />
    </div>
  );
}

/* Un elenco che l'app già conosce, con le spunte: personale o mezzi. */
function Spunte({ titolo, voci, scelti, onChange, etichetta, sotto, testid }) {
  const [cerca, setCerca] = useState("");
  const filtrate = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    if (!q) return voci;
    return voci.filter((v) => `${etichetta(v)} ${sotto ? sotto(v) : ""}`
      .toLowerCase().includes(q));
  }, [voci, cerca, etichetta, sotto]);
  const tutti = scelti.length === voci.length && voci.length > 0;

  return (
    <div className="border border-slate-200 rounded-lg" data-testid={testid}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
        <span className="font-medium text-sm text-slate-700">{titolo}</span>
        <span className="text-xs text-slate-500">{scelti.length} di {voci.length}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
                   placeholder="Cerca" className="h-8 pl-7 w-40 text-sm" />
          </div>
          <Button type="button" variant="outline" size="sm"
                  onClick={() => onChange(tutti ? [] : voci.map((v) => v.id))}>
            {tutti ? "Nessuno" : "Tutti"}
          </Button>
        </div>
      </div>
      <div className="max-h-56 overflow-y-auto p-2 grid sm:grid-cols-2 gap-1">
        {filtrate.map((v) => (
          <label key={v.id}
                 className="flex items-start gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" className="mt-1"
                   checked={scelti.includes(v.id)}
                   onChange={(e) => onChange(e.target.checked
                     ? [...scelti, v.id]
                     : scelti.filter((x) => x !== v.id))} />
            <span className="text-sm leading-tight">
              {etichetta(v)}
              {sotto && <span className="block text-xs text-slate-500">{sotto(v)}</span>}
            </span>
          </label>
        ))}
        {filtrate.length === 0 && (
          <p className="text-sm text-slate-400 px-2 py-3">Nessun risultato.</p>
        )}
      </div>
    </div>
  );
}

/* Una tabella che si compila riga per riga: fasi, noli, saldatori. */
function Righe({ titolo, colonne, righe, onChange, nuova, testid }) {
  const cambia = (i, chiave, valore) =>
    onChange(righe.map((r, n) => (n === i ? { ...r, [chiave]: valore } : r)));
  return (
    <div className="border border-slate-200 rounded-lg" data-testid={testid}>
      <div className="flex items-center px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
        <span className="font-medium text-sm text-slate-700">{titolo}</span>
        <Button type="button" variant="outline" size="sm" className="ml-auto"
                onClick={() => onChange([...righe, nuova()])}>
          <Plus className="w-3.5 h-3.5 mr-1" />Riga
        </Button>
      </div>
      <div className="p-2 space-y-1">
        {righe.length === 0 && (
          <p className="text-sm text-slate-400 px-1 py-2">
            Vuota: sul PDF escono le righe da compilare a penna.
          </p>
        )}
        {righe.map((r, i) => (
          <div key={i} className="flex items-center gap-1">
            {colonne.map((c) => (
              c.tipo === "spunta" ? (
                <label key={c.chiave} className="flex flex-col items-center px-1"
                       title={c.etichetta}>
                  <span className="text-[10px] text-slate-500">{c.etichetta}</span>
                  <input type="checkbox" checked={!!r[c.chiave]}
                         onChange={(e) => cambia(i, c.chiave, e.target.checked)} />
                </label>
              ) : (
                <Input key={c.chiave} type={c.tipo || "text"} value={r[c.chiave] || ""}
                       placeholder={c.etichetta} className={`h-9 ${c.classe || "flex-1"}`}
                       onChange={(e) => cambia(i, c.chiave, e.target.value)} />
              )
            ))}
            <Button type="button" variant="ghost" size="sm"
                    onClick={() => onChange(righe.filter((_, n) => n !== i))}>
              <X className="w-4 h-4 text-slate-400" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PosAppalto() {
  const [dati, setDati] = useState(null);
  const [rif, setRif] = useState(null);
  const [carico, setCarico] = useState(true);
  const [form, setForm] = useState(null);
  const [impostazioni, setImpostazioni] = useState(null);
  const [daEmettere, setDaEmettere] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);
  const [valutazioni, setValutazioni] = useState(null);
  const selettore = useRef(null);
  const carica = useCallback(async () => {
    try {
      const [e, r] = await Promise.all([
        api.get("/pos-appalto"), api.get("/pos-appalto/riferimenti"),
      ]);
      setDati(e.data);
      setRif(r.data);
    } catch {
      toast.error("Errore nel caricamento dei POS di appalto");
    } finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const c = (chiave) => ({
    valore: form?.[chiave],
    onChange: (v) => setForm((f) => ({ ...f, [chiave]: v })),
  });

  const salva = async () => {
    if (!form.committente?.trim()) {
      toast.error("Il committente serve: è a lui che si manda il POS");
      return;
    }
    try {
      const corpo = { ...form };
      if (form.id) await api.put(`/pos-appalto/${form.id}`, corpo);
      else await api.post("/pos-appalto", corpo);
      toast.success(form.id ? "POS aggiornato" : "POS di appalto creato");
      setForm(null);
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const stampa = async (p, conCorpo = true) => {
    try {
      const { data, headers } = await api.get(
        `/pos-appalto/${p.id}/pdf?con_corpo=${conCorpo}`, { responseType: "blob" });
      const cd = headers["content-disposition"] || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1]
        || `POS appalto ${p.numero}.pdf`;
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella stampa"); }
  };

  const emetti = async () => {
    const p = daEmettere;
    setDaEmettere(null);
    try {
      const { data } = await api.post(`/pos-appalto/${p.id}/emetti`);
      toast.success(`POS ${p.numero} emesso in revisione 0 · ${data.riemesso ? "riemesso" : "prima emissione"}`);
      carica();
      stampa({ ...p, revisione: data.revisione });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'emissione");
    }
  };

  const elimina = async () => {
    const p = daEliminare;
    setDaEliminare(null);
    try {
      await api.delete(`/pos-appalto/${p.id}`);
      toast.success(`POS ${p.numero} eliminato`);
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const salvaValutazioni = async () => {
    try {
      const { data } = await api.put("/pos-appalto/valutazioni",
                                     { valutazioni });
      toast.success("Valutazioni aggiornate");
      setValutazioni(data.valutazioni);
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const salvaImpostazioni = async () => {
    try {
      await api.put("/pos-appalto/impostazioni", impostazioni);
      toast.success("Dati dell'impresa aggiornati");
      setImpostazioni(null);
      carica();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const scaricaFile = (data, tipo, nome) => {
    const url = URL.createObjectURL(new Blob([data], { type: tipo }));
    const a = document.createElement("a");
    a.href = url; a.download = nome;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Il corpo in Word: è il documento su cui si lavora. Si scarica già
  // corretto e rinumerato, si modifica, si ricarica.
  const scaricaCorpoWord = async () => {
    try {
      const { data } = await api.get("/pos-appalto/corpo/word",
                                     { responseType: "blob" });
      scaricaFile(data, "application/vnd.openxmlformats-officedocument."
                        + "wordprocessingml.document",
                  "POS appalto - corpo.docx");
    } catch { toast.error("Errore nello scaricamento"); }
  };

  const caricaCorpo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/pos-appalto/corpo", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.origine === "word"
        ? `Corpo caricato: ${data.pagine} pagine, ${data.sezioni?.length || 0} parti. `
          + "Numerazione e indici li rifà l'app."
        : `Corpo caricato: ${data.pagine} pagine (PDF già impaginato)`);
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    }
  };

  if (carico) return <div className="p-6 text-slate-500">Caricamento…</div>;

  const corpo = rif?.corpo_standard;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="pagina-pos-appalto">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">POS di appalto</h1>
          <p className="text-sm text-slate-500">
            Il piano operativo generale, quello che si consegna al committente
            all'avvio del contratto.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setImpostazioni(rif.impresa)}
                  data-testid="apri-impostazioni">
            <Settings2 className="w-4 h-4 mr-1" />Dati impresa
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600"
                  onClick={() => setForm(vuoto())} data-testid="nuovo-pos-appalto">
            <Plus className="w-4 h-4 mr-1" />Nuovo POS
          </Button>
        </div>
      </div>

      {/* Le valutazioni specifiche: è la sezione 4 del piano, e insieme lo
          scadenzario di quei documenti. Sta in alto perché è la cosa che
          scade, e che scadendo rende il POS non più veritiero. */}
      <Card className={(rif?.valutazioni || []).some((v) => v.stato === "scaduta")
        ? "border-red-300" : ""}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-[#0067b2] shrink-0" />
            <div>
              <span className="font-medium text-slate-700">
                Valutazioni dei rischi specifici
              </span>
              <span className="block text-xs text-slate-500">
                Rumore, vibrazioni, chimico e le altre. Finiscono nella SEZ.4 del
                POS con titolo, riferimento e data; l'originale resta in sede.
              </span>
            </div>
            <Button variant="outline" className="ml-auto"
                    onClick={() => setValutazioni(
                      (rif?.valutazioni || []).map((v) => ({ ...v })))}
                    data-testid="apri-valutazioni">
              <Settings2 className="w-4 h-4 mr-1" />Gestisci
            </Button>
          </div>

          {(rif?.valutazioni || []).length === 0 ? (
            <p className="text-sm text-amber-700 mt-3">
              Nessuna valutazione indicata: la SEZ.4 del POS esce vuota.
            </p>
          ) : (
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {rif.valutazioni.map((v, i) => {
                const s = STATO_VALUTAZIONE[v.stato] || STATO_VALUTAZIONE.valida;
                return (
                  <div key={i} className={`border rounded-lg px-3 py-2 ${s.classe}`}
                       data-testid={`valutazione-${i}`}>
                    <div className="text-sm font-medium leading-tight">{v.titolo}</div>
                    <div className="text-xs opacity-80 mt-0.5">
                      {v.riferimento || "—"}
                    </div>
                    <div className="text-xs mt-1">
                      emessa {itDate(v.emesso_il)} · {s.testo}
                      {v.scadenza && ` il ${itDate(v.scadenza)}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Il corpo del piano: le pagine che non cambiano da un appalto
          all'altro. Caricato in Word, è l'app a stamparlo — rinumera i
          capitoli, rifà gli indici e mette i numeri di pagina giusti. */}
      <Card className={corpo?.presente ? "" : "border-amber-300 bg-amber-50"}>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          {corpo?.presente ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          )}
          <div className="text-sm">
            {corpo?.presente ? (
              <>
                <span className="font-medium text-slate-700">
                  Corpo del piano: {corpo.pagine} pagine
                  {corpo.origine === "word" ? " — stampate dall'app" : " — PDF già impaginato"}
                </span>
                <span className="block text-xs text-slate-500">
                  da «{corpo.nome}», caricato il {itDate(corpo.caricato_il)}.
                  {corpo.origine === "word"
                    ? " Numerazione dei capitoli, indici delle sezioni e numeri di pagina li calcola l'app a ogni stampa."
                    : " L'app ci stampa sopra solo i numeri di pagina: quello che c'è scritto dentro resta com'è."}
                </span>
                {corpo.origine === "word" && corpo.sezioni?.length > 0 && (
                  <span className="block text-xs text-slate-500 mt-1">
                    {corpo.sezioni.map((s) => `${s.sigla || "Metodologia"}: ${s.capitoli} capitoli`).join(" · ")}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="font-medium text-amber-800">
                  Corpo del piano non ancora caricato
                </span>
                <span className="block text-xs text-amber-700">
                  Carica il Word del corpo — metodologia, indagine fonometrica
                  e le tre sezioni — e l'app lo stampa in coda a ogni piano,
                  con i suoi indici e i numeri di pagina del documento finito.
                </span>
              </>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <input type="file" ref={selettore} className="hidden"
                   accept=".docx,.docm,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                   onChange={caricaCorpo} />
            {corpo?.origine === "word" && (
              <Button variant="outline" onClick={scaricaCorpoWord}
                      data-testid="scarica-corpo-word">
                <Download className="w-4 h-4 mr-1" />
                Scarica in Word
              </Button>
            )}
            <Button variant="outline" onClick={() => selettore.current?.click()}
                    data-testid="carica-corpo">
              <Upload className="w-4 h-4 mr-1" />
              {corpo?.presente ? "Sostituisci" : "Carica"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Numero</th>
                <th className="text-left px-4 py-2 font-medium">Committente</th>
                <th className="text-left px-4 py-2 font-medium">Oggetto</th>
                <th className="text-left px-4 py-2 font-medium">CIG</th>
                <th className="text-left px-4 py-2 font-medium">Stato</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {(dati?.pos || []).map((p) => (
                <tr key={p.id} className="border-t border-slate-100"
                    data-testid={`riga-pos-${p.id}`}>
                  <td className="px-4 py-2 font-medium text-slate-700">{p.numero}</td>
                  <td className="px-4 py-2">{p.committente || "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{p.opera || "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{p.cig || "—"}</td>
                  <td className="px-4 py-2">
                    {p.emesso_il ? (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                        Rev. {p.revisione} del {itDate(p.emesso_il)}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">
                        Bozza
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" title="Anteprima"
                              onClick={() => stampa(p)}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Modifica"
                              onClick={() => setForm({ ...vuoto(), ...p })}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm"
                              onClick={() => setDaEmettere(p)}
                              data-testid={`emetti-${p.id}`}>
                        <FileSignature className="w-4 h-4 mr-1" />
                        {p.emesso_il ? "Riemetti" : "Emetti"}
                      </Button>
                      {!p.emesso_il && (
                        <Button variant="ghost" size="sm" title="Elimina"
                                onClick={() => setDaEliminare(p)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(dati?.pos || []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  Nessun POS di appalto. Il primo si fa con «Nuovo POS».
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ------------------------------------------------ il modulo del POS */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {form?.id ? `POS di appalto ${form.numero}` : "Nuovo POS di appalto"}
            </DialogTitle>
          </DialogHeader>

          {form && (
            <div className="space-y-5">
              <div>
                <h3 className="font-medium text-slate-700 mb-2">L'appalto</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-slate-500">Committente</Label>
                    <select
                      className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm"
                      value={form.committente_id || ""}
                      data-testid="scegli-committente"
                      onChange={(e) => {
                        const scelto = (rif?.committenti || [])
                          .find((x) => x.id === e.target.value);
                        setForm((f) => ({ ...f, committente_id: e.target.value,
                                          committente: scelto?.name || f.committente }));
                      }}>
                      <option value="">— scrivilo a mano qui sotto —</option>
                      {(rif?.committenti || []).map((x) => (
                        <option key={x.id} value={x.id}>{x.name}</option>
                      ))}
                    </select>
                  </div>
                  <Campo etichetta="Committente (come va stampato)" {...c("committente")} larga />
                  <Campo etichetta="Nella persona di" {...c("committente_persona")} />
                  <Campo etichetta="Qualifica" {...c("committente_qualifica")} />
                  <Campo etichetta="Opera da realizzare / oggetto dell'appalto"
                         {...c("opera")} larga />
                  <Campo etichetta="Indirizzo o ambito del cantiere" {...c("indirizzo")} larga />
                  <Campo etichetta="Contratto n°" {...c("contratto")} />
                  <Campo etichetta="Codice CIG" {...c("cig")} />
                  <Campo etichetta="Ordine di lavoro" {...c("ordine_lavoro")} />
                  <Campo etichetta="PL n°" {...c("pl_numero")} />
                  <Campo etichetta="Responsabile dei lavori" {...c("responsabile_lavori")} />
                  <Campo etichetta="CSP" {...c("csp")} />
                  <Campo etichetta="CSE" {...c("cse")} />
                  <Campo etichetta="Eventuale subappalto" {...c("subappalto")} />
                  <Campo etichetta="Campo di applicazione subappaltatore"
                         {...c("campo_subappalto")} larga />
                </div>
              </div>

              <Spunte titolo="Personale impiegato nell'appalto" testid="spunte-personale"
                      voci={rif?.personale || []} scelti={form.personale_ids || []}
                      onChange={(v) => setForm((f) => ({ ...f, personale_ids: v }))}
                      etichetta={(x) => x.full_name} sotto={(x) => x.role} />

              <Spunte titolo="Mezzi e attrezzature" testid="spunte-mezzi"
                      voci={rif?.mezzi || []} scelti={form.mezzi_ids || []}
                      onChange={(v) => setForm((f) => ({ ...f, mezzi_ids: v }))}
                      etichetta={nomeMezzo} sotto={(x) => x.identifier} />

              {/* I saldatori: l'app sa chi ha il patentino a fascicolo, ma
                  quali procedimenti copra lo dice solo il patentino stesso.
                  Quindi propone i nomi e le crocette si mettono a mano. */}
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="font-medium text-slate-700">Qualifiche di saldatura</h3>
                  {(rif?.saldatori || [])
                    .filter((s) => !(form.saldatori || []).some((x) => x.nome === s.nome))
                    .map((s) => (
                      <Button key={s.id} type="button" variant="outline" size="sm"
                              className={s.scaduto ? "border-red-300 text-red-600" : ""}
                              onClick={() => setForm((f) => ({ ...f, saldatori: [
                                ...(f.saldatori || []),
                                { nome: s.nome, acc_el: false, acc_oss: false, pe: false,
                                  neg: false, pos: false,
                                  note: s.scadenza ? `scad. ${itDate(s.scadenza)}` : "" }] }))}>
                        <Plus className="w-3.5 h-3.5 mr-1" />{s.nome}
                        {s.scaduto && " (scaduto)"}
                      </Button>
                    ))}
                </div>
                <Righe titolo="Saldatori dichiarati" testid="righe-saldatori"
                       righe={form.saldatori || []}
                       onChange={(v) => setForm((f) => ({ ...f, saldatori: v }))}
                       nuova={() => ({ nome: "" })}
                       colonne={[
                         { chiave: "nome", etichetta: "Nominativo", classe: "flex-[2]" },
                         ...(rif?.qualifiche || []).map((q) => (
                           { chiave: q.codice, etichetta: q.nome, tipo: "spunta" })),
                         { chiave: "note", etichetta: "Note" },
                       ]} />
              </div>

              <Righe titolo="Mezzi in nolo a freddo" testid="righe-noleggio"
                     righe={form.noleggio || []}
                     onChange={(v) => setForm((f) => ({ ...f, noleggio: v }))}
                     nuova={() => ({ tipo: "", targa: "", utilizzatore: "" })}
                     colonne={[
                       { chiave: "tipo", etichetta: "Tipo", classe: "flex-[2]" },
                       { chiave: "targa", etichetta: "Targa o matricola" },
                       { chiave: "utilizzatore", etichetta: "Utilizzatore" },
                     ]} />

              <Righe titolo="Programma dei lavori" testid="righe-fasi"
                     righe={form.fasi || []}
                     onChange={(v) => setForm((f) => ({ ...f, fasi: v }))}
                     nuova={() => ({ fase: "", inizio: "", fine: "", interferenza: "" })}
                     colonne={[
                       { chiave: "fase", etichetta: "Fase lavorativa", classe: "flex-[2]" },
                       { chiave: "inizio", etichetta: "Inizio", tipo: "date", classe: "w-36" },
                       { chiave: "fine", etichetta: "Fine", tipo: "date", classe: "w-36" },
                       { chiave: "interferenza", etichetta: "Interferenza con" },
                     ]} />

              <div>
                <Label className="text-xs text-slate-500">Note</Label>
                <Textarea rows={3} className="mt-1" value={form.note || ""}
                          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Annulla</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={salva}
                    data-testid="salva-pos-appalto">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----------------------------------- le valutazioni dei rischi */}
      <Dialog open={!!valutazioni} onOpenChange={(v) => !v && setValutazioni(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Valutazioni dei rischi specifici</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Solo gli estremi: titolo, riferimento, data di emissione e quando va
            rifatta. I documenti restano in sede — qui servono per la SEZ.4 del
            POS e per non farsi trovare con una valutazione scaduta.
          </p>
          {valutazioni && (
            <Righe titolo="Elenco" testid="righe-valutazioni"
                   righe={valutazioni} onChange={setValutazioni}
                   nuova={() => ({ titolo: "", riferimento: "",
                                   emesso_il: "", scadenza: "" })}
                   colonne={[
                     { chiave: "titolo", etichetta: "Valutazione", classe: "flex-[2]" },
                     { chiave: "riferimento", etichetta: "Riferimento", classe: "flex-[2]" },
                     { chiave: "emesso_il", etichetta: "Emessa il", tipo: "date", classe: "w-36" },
                     { chiave: "scadenza", etichetta: "Prossima revisione", tipo: "date", classe: "w-36" },
                   ]} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setValutazioni(null)}>Annulla</Button>
            <Button className="bg-orange-500 hover:bg-orange-600"
                    onClick={salvaValutazioni} data-testid="salva-valutazioni">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------- i dati fissi d'impresa */}
      <Dialog open={!!impostazioni} onOpenChange={(v) => !v && setImpostazioni(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dati dell'impresa sul POS</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Finiscono nel riquadro «Dati dell'impresa appaltatrice». Il numero
            dei dipendenti lo conta l'app da sé.
          </p>
          {impostazioni && (
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["ragione_sociale", "Ragione sociale", true],
                ["telefono", "Telefono"],
                ["partita_iva", "Partita IVA e codice fiscale"],
                ["pos_inail", "Posizione INAIL"],
                ["pos_inps", "Posizione INPS"],
                ["cert_9001", "Certificato ISO 9001"],
                ["cert_14001", "Certificato ISO 14001"],
                ["cert_45001", "Certificato ISO 45001"],
                ["cert_soa", "Certificato SOA"],
                ["asl", "ASL competente"],
                ["amministratore", "Amministratore unico"],
                ["medico_competente", "Medico competente"],
                ["rspp", "RSPP"],
                ["rls", "RLS"],
                ["responsabile_sicurezza_cantiere",
                 "Responsabile sicurezza in cantiere"],
                ["ccnl", "CCNL applicato", true],
                ["polizza_rct_rco", "Polizza RCT / RCO"],
                ["orari", "Orari e turni di lavoro", true],
              ].map(([chiave, etichetta, larga]) => (
                <Campo key={chiave} etichetta={etichetta} larga={larga}
                       valore={impostazioni[chiave]}
                       onChange={(v) => setImpostazioni((s) => ({ ...s, [chiave]: v }))} />
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpostazioni(null)}>Annulla</Button>
            <Button className="bg-orange-500 hover:bg-orange-600"
                    onClick={salvaImpostazioni} data-testid="salva-impostazioni">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------- l'emissione */}
      <Dialog open={!!daEmettere} onOpenChange={(v) => !v && setDaEmettere(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {daEmettere?.emesso_il ? "Riemettere il POS?" : "Emettere il POS?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {daEmettere?.emesso_il ? (
              <>Il POS {daEmettere.numero} viene rifatto da capo con la data di
                oggi, e resta in <b>revisione 0</b>: questo piano si emette per
                l'appalto, e se viene contestato se ne emette uno nuovo, non si
                corregge quello vecchio. Il file precedente viene sostituito.</>
            ) : (
              <>Il POS {daEmettere?.numero} viene emesso in <b>revisione 0</b> con
                la data di oggi, firmato dal datore di lavoro, e viene conservato
                così com'è. Da quel momento non si può più eliminare.</>
            )}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDaEmettere(null)}>Annulla</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={emetti}
                    data-testid="conferma-emetti">
              Emetti e scarica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!daEliminare} onOpenChange={(v) => !v && setDaEliminare(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Eliminare la bozza?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            La bozza {daEliminare?.numero} viene eliminata e non si recupera.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDaEliminare(null)}>Annulla</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={elimina}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
